import type { CompanyInfo } from '@mt/shared';
import * as DM from '@radix-ui/react-dropdown-menu';
import { Smile } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { Customer } from '@/lib/types';
import { cn } from '@/lib/utils';
import { parseCompanyImage } from '../quick-quote/api';
import { CustomerPasteDialog, customerFieldsOf } from '../quick-quote/CustomerPasteDialog';
import { useLinkCustomer, type ZaloConversation } from './api';
import { fetchInboxImage, stashInboxHandoff, type InboxImageRef } from './inbox-bridge';

/**
 * Bộ icon vui nhộn khi soạn tin (anh Tiến 10/10/2026). Chỉ emoji chữ (gửi được qua mọi kênh: Zalo, OA, Messenger, web, email);
 * nhãn dán (sticker) Zalo chưa làm vì cần bộ sticker riêng của Zalo.
 */
const EMOJI_GROUPS: { label: string; items: string[] }[] = [
  { label: 'Vui vẻ', items: ['😀', '😁', '😂', '🤣', '😊', '😍', '🥰', '😘', '😎', '🤗', '🤩', '😉', '😜', '🤪', '😇', '🥳'] },
  { label: 'Cảm ơn · đồng ý', items: ['🙏', '👍', '👌', '👏', '🙌', '💪', '🤝', '✌️', '❤️', '💯', '✅', '☑️', '🌹', '💐', '🎉', '🎊'] },
  { label: 'Hỏi · chờ', items: ['🤔', '😅', '😢', '😭', '😱', '😴', '🙄', '😬', '🤦', '🤷', '⏳', '⏰', '❓', '❗', '⚠️', '🆗'] },
  { label: 'Công việc', items: ['📦', '🚚', '🚛', '🏗️', '🔧', '🔩', '⚙️', '🧯', '🔥', '💧', '📄', '📋', '💰', '💵', '📞', '📍'] },
];

export function EmojiPicker({ onPick, disabled }: { onPick: (e: string) => void; disabled?: boolean }) {
  return (
    // Không chặn phần còn lại: bấm ra ngoài (ô nhập, khung chat) là đóng
    <DM.Root modal={false}>
      <DM.Trigger
        disabled={disabled}
        className="rounded-md p-1.5 text-muted-foreground outline-none hover:bg-muted disabled:opacity-50"
        aria-label="Chèn icon"
        title="Chèn icon"
      >
        <Smile className="size-4" />
      </DM.Trigger>
      <DM.Portal>
        <DM.Content align="end" side="top" sideOffset={6} className="z-50 w-72 rounded-lg border bg-card p-2 shadow-lg">
          {EMOJI_GROUPS.map((g) => (
            <div key={g.label} className="mb-1.5 last:mb-0">
              <div className="px-1 pb-0.5 text-[11px] text-muted-foreground">{g.label}</div>
              <div className="grid grid-cols-8">
                {g.items.map((e) => (
                  <DM.Item
                    key={e}
                    onSelect={(ev) => {
                      // Giữ khung mở để chèn nhiều icon liên tiếp
                      ev.preventDefault();
                      onPick(e);
                    }}
                    className="flex size-8 cursor-pointer items-center justify-center rounded text-lg outline-none data-highlighted:bg-muted"
                  >
                    {e}
                  </DM.Item>
                ))}
              </div>
            </div>
          ))}
        </DM.Content>
      </DM.Portal>
    </DM.Root>
  );
}

/** Chèn chữ vào ô nhập tại vị trí con trỏ */
export function insertAtCaret(el: HTMLTextAreaElement | null, cur: string, add: string): { text: string; caret: number } {
  const start = el?.selectionStart ?? cur.length;
  const end = el?.selectionEnd ?? cur.length;
  return { text: cur.slice(0, start) + add + cur.slice(end), caret: start + add.length };
}

/**
 * Thao tác với ảnh khách gửi (anh Tiến 10/10/2026): đọc thông tin khách → gắn khách có sẵn / tự lưu khách mới (mã tạm TN-),
 * mở Báo giá nhanh hoặc Mua hàng nhanh với ảnh đó.
 */
export function useImageActions(c: ZaloConversation | undefined) {
  const { can } = useAuth();
  const navigate = useNavigate();
  const link = useLinkCustomer();
  const [busy, setBusy] = useState<string | null>(null);
  const [info, setInfo] = useState<CompanyInfo | null>(null);

  const load = async (key: string, src: InboxImageRef | File) => {
    setBusy(key);
    try {
      return src instanceof File ? src : await fetchInboxImage(src);
    } catch (e) {
      toast.error((e as Error).message);
      return null;
    } finally {
      setBusy(null);
    }
  };

  const recognize = async (src: InboxImageRef | File) => {
    const file = await load('customer', src);
    if (!file) return;
    setBusy('customer');
    try {
      const r = await parseCompanyImage(file);
      if (!r.info) return void toast.info('Ảnh không có thông tin công ty (tên, MST, địa chỉ, SĐT). Nếu là danh sách hàng, bấm Báo giá nhanh.');
      setInfo(r.info);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const attach = async (cust: Customer) => {
    setInfo(null);
    if (!c) return;
    try {
      await link.mutateAsync({ id: c.id, customerId: cust.id });
      toast.success(`Đã gắn khách ${cust.code} – ${cust.name} vào hội thoại`);
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  /** Không trùng mà tự lưu lỗi / NV bấm "Tạo khách mới": tạo khách (mã tạm TN-) rồi gắn */
  const createAndAttach = async (i: CompanyInfo) => {
    setInfo(null);
    try {
      const f = customerFieldsOf(i);
      const cust = await api.post<Customer>('/customers', { ...f, email: f.email || null, note: f.note || null, customerType: 'company', contacts: [] });
      toast.success(`Đã lưu khách mới ${cust.code} – ${cust.name}`);
      await attach(cust);
    } catch (e) {
      toast.error(`Chưa lưu được khách: ${(e as Error).message}. Mở Khách hàng để thêm tay.`);
    }
  };

  const open = async (src: InboxImageRef | File, to: 'quote' | 'purchase') => {
    const file = await load(to, src);
    if (!file) return;
    stashInboxHandoff(file, to === 'quote' ? (c?.customerId ?? null) : null);
    navigate(to === 'quote' ? '/bao-gia-nhanh' : '/mua-hang-nhanh');
  };

  const dialog = (
    <CustomerPasteDialog
      info={info}
      auto={can('customer.create')}
      onClose={() => setInfo(null)}
      onUse={(x) => void attach(x)}
      onCreate={(i) => void createAndAttach(i)}
    />
  );
  return {
    busy,
    // Đọc ảnh dùng chung máy đọc của Báo giá nhanh (quyền lập báo giá)
    recognize: can('quote.create') ? recognize : null,
    toQuote: can('quote.create') ? (s: InboxImageRef | File) => void open(s, 'quote') : null,
    toPurchase: can('purchase.manage') ? (s: InboxImageRef | File) => void open(s, 'purchase') : null,
    dialog,
  };
}

export type ImageActions = ReturnType<typeof useImageActions>;

/** Nút nhỏ hiện khi rê chuột lên ảnh trong tin */
export function ImageActionBar({ actions, src, className }: { actions: ImageActions; src: InboxImageRef; className?: string }) {
  const btn =
    'rounded-md bg-card/95 px-1.5 py-0.5 text-[11px] font-medium text-foreground shadow-sm hover:bg-primary hover:text-primary-foreground disabled:opacity-60';
  const b = actions.busy !== null;
  return (
    <div className={cn('flex flex-wrap gap-1', className)}>
      {actions.recognize && (
        <button
          type="button"
          className={btn}
          disabled={b}
          onClick={() => void actions.recognize!(src)}
          title="AI đọc tên, MST, SĐT, địa chỉ trong ảnh rồi gắn khách"
        >
          {actions.busy === 'customer' ? 'Đang đọc…' : 'Đọc khách'}
        </button>
      )}
      {actions.toQuote && (
        <button type="button" className={btn} disabled={b} onClick={() => actions.toQuote!(src)} title="Mở Báo giá nhanh với ảnh này">
          {actions.busy === 'quote' ? 'Đang tải…' : 'BG nhanh'}
        </button>
      )}
      {actions.toPurchase && (
        <button type="button" className={btn} disabled={b} onClick={() => actions.toPurchase!(src)} title="Mở Mua hàng nhanh với ảnh này (phiếu NCC gửi)">
          {actions.busy === 'purchase' ? 'Đang tải…' : 'Mua nhanh'}
        </button>
      )}
    </div>
  );
}
